import React, { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import apiClient from "../api/apiClient";
import axios from "axios"; // Import axios directly
import { Helmet } from "react-helmet-async";

const CourseDetails = () => {
  const [formation, setFormation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const { id } = useParams();
  const navigate = useNavigate();

  const fetchCourseDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      // Add timeout using axios directly
      const source = axios.CancelToken.source();
      const timeoutId = setTimeout(
        () => source.cancel("Request timeout"),
        10000
      );
      const response = await apiClient.get(`/formations/${id}`, {
        cancelToken: source.token,
      });
      clearTimeout(timeoutId);
      if (response.data) {
        setFormation(response.data);
      } else {
        throw new Error("Formation non trouvée");
      }
    } catch (err) {
      if (axios.isCancel(err)) {
        // Use axios.isCancel directly
        setError(
          new Error(
            "Délai d'attente dépassé : Veuillez vérifier votre connexion"
          )
        );
      } else if (err.response) {
        if (err.response.status === 404) {
          setError(new Error("Formation non trouvée"));
        } else {
          setError(
            new Error(
              `Erreur du serveur : ${err.response.status} - ${
                err.response.data?.message || "Erreur inconnue"
              }`
            )
          );
        }
      } else if (err.request) {
        setError(
          new Error(
            "Erreur réseau : Impossible d'atteindre le serveur. Veuillez vérifier votre connexion."
          )
        );
      } else {
        setError(new Error(`Erreur : ${err.message}`));
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCourseDetails();
  }, [id, retryCount]);

  const handleRetry = () => {
    setRetryCount((prev) => prev + 1);
  };

  // Show full page loading
  if (loading && retryCount === 0) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: "100vh" }}
      >
        <div className="text-center">
          <div
            className="spinner-border text-primary"
            role="status"
            style={{ width: "3rem", height: "3rem" }}
          >
            <span className="visually-hidden">Chargement...</span>
          </div>
          <p className="mt-3">Chargement des détails de la formation...</p>
        </div>
      </div>
    );
  }

  // Show full page error if no formation
  if (error && !formation) {
    return (
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-lg-6">
            <div className="alert alert-danger mt-5" role="alert">
              <div className="text-center">
                <i className="fas fa-exclamation-triangle fa-3x mb-3 text-danger"></i>
                <h4>Erreur de chargement</h4>
                <p className="mb-3">{error.message}</p>
                <div className="d-flex justify-content-center gap-2">
                  <button
                    className="btn btn-primary"
                    onClick={handleRetry}
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                        ></span>{" "}
                        Chargement...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-redo me-2"></i> Réessayer
                      </>
                    )}
                  </button>
                  <Link to="/courses" className="btn btn-outline-secondary">
                    <i className="fas fa-arrow-left me-2"></i> Retour aux cours
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!formation) {
    return (
      <div className="container py-5 text-center">
        <div className="alert alert-warning">
          <h4>Formation non trouvée</h4>
          <p>
            {" "}
            La formation que vous recherchez n'existe pas ou a été supprimée.{" "}
          </p>
          <Link to="/courses" className="btn btn-primary">
            <i className="fas fa-arrow-left me-2"></i> Retour aux cours
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{formation.nom_formation} – DVELGE Formation</title>
        <meta
          name="description"
          content={formation.description_formation?.slice(0, 160)}
        />
        <meta
          name="keywords"
          content={`formation ${formation.nom_formation}, certification ${formation.nom_formation}, cours informatique Conakry`}
        />
        {/* OG Meta */}
        <meta
          property="og:title"
          content={`${formation.nom_formation} – DVELGE Formation`}
        />
        <meta
          property="og:description"
          content={formation.description_formation?.slice(0, 160)}
        />
        <meta
          property="og:image"
          content={
            formation.image_formation
              ? `/uploads/${formation.image_formation}`
              : "/assets/img/preview.png"
          }
        />
        <meta
          property="og:url"
          content={`https://formation.dvelgegroup.com/courses/${id}`}
        />
        <meta property="og:type" content="website" />
        {/* Twitter */}
        <meta
          name="twitter:title"
          content={`${formation.nom_formation} – DVELGE Formation`}
        />
        <meta
          name="twitter:description"
          content={formation.description_formation?.slice(0, 160)}
        />
        <meta
          name="twitter:image"
          content={
            formation.image_formation
              ? `/uploads/${formation.image_formation}`
              : "/assets/img/preview.png"
          }
        />
        {/* JSON-LD Structured Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Course",
            name: formation.nom_formation,
            description: formation.description_formation,
            image: formation.image_formation
              ? `/uploads/${formation.image_formation}`
              : "https://formation.dvelgegroup.com/assets/img/preview.png",
            provider: {
              "@type": "Organization",
              name: "DVELGE Formation",
              sameAs: "https://formation.dvelgegroup.com",
            },
            hasCourseInstance: {
              "@type": "CourseInstance",
              courseMode: formation.mode_livraison || "In-person or Online",
              location: {
                "@type": "Place",
                name: "Conakry, Guinea",
              },
              offers: {
                "@type": "Offer",
                price: formation.prix_formation,
                priceCurrency: "GNF",
                availability: "https://schema.org/InStock",
              },
            },
            sku: `COURSE-${id}`,
          })}
        </script>
      </Helmet>

      <main className="main-area fix">
        {/* Show warning if there was an error but we have data */}
        {error && (
          <div className="container mt-4">
            <div className="alert alert-warning" role="alert">
              <div className="d-flex justify-content-between align-items-center">
                <small>
                  <i className="fas fa-exclamation-triangle me-2"></i> Données
                  en cache: {error.message}
                </small>
                <button
                  className="btn btn-sm btn-outline-primary"
                  onClick={handleRetry}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                      ></span>{" "}
                      Chargement...
                    </>
                  ) : (
                    "Actualiser"
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Loading state during retry */}
        {loading && retryCount > 0 && (
          <div className="container">
            <div className="row justify-content-center">
              <div className="col-12 text-center">
                <div className="d-flex justify-content-center align-items-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Chargement...</span>
                  </div>
                  <span className="ms-3">
                    {" "}
                    Mise à jour des détails de la formation...{" "}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {!loading && formation && (
          <>
            <div className="breadcrumb__area breadcrumb__bg breadcrumb__bg-two">
              <div className="container">
                <div className="row">
                  <div className="col-12">
                    <div className="breadcrumb__content">
                      <nav className="breadcrumb">
                        <span property="itemListElement" typeof="ListItem">
                          <Link to="/">Accueil</Link>
                        </span>
                        <span className="breadcrumb-separator">
                          <i className="fas fa-angle-right"></i>
                        </span>
                        <span property="itemListElement" typeof="ListItem">
                          <Link to="/courses">Cours</Link>
                        </span>
                        <span className="breadcrumb-separator">
                          <i className="fas fa-angle-right"></i>
                        </span>
                        <span property="itemListElement" typeof="ListItem">
                          {formation.nom_formation}
                        </span>
                      </nav>
                    </div>
                  </div>
                </div>
              </div>
              <div className="breadcrumb__shape-wrap">
                <img
                  src="assets/img/others/breadcrumb_shape01.svg"
                  alt=""
                  className="alltuchtopdown"
                />
                <img
                  src="assets/img/others/breadcrumb_shape02.svg"
                  alt=""
                  data-aos="fade-right"
                  data-aos-delay="300"
                />
                <img
                  src="assets/img/others/breadcrumb_shape03.svg"
                  alt=""
                  data-aos="fade-up"
                  data-aos-delay="400"
                />
                <img
                  src="assets/img/others/breadcrumb_shape04.svg"
                  alt=""
                  data-aos="fade-down-left"
                  data-aos-delay="400"
                />
                <img
                  src="assets/img/others/breadcrumb_shape05.svg"
                  alt=""
                  data-aos="fade-left"
                  data-aos-delay="400"
                />
              </div>
            </div>

            <section className="courses__details-area section-py-120">
              <div className="container">
                <div className="row">
                  <div className="col-xl-9 col-lg-8">
                    <div className="courses__details-thumb">
                      <img
                        src={
                          formation.image_formation
                            ? `${process.env.REACT_APP_API_URL}/uploads/${formation.image_formation}`
                            : "assets/img/courses/course_thumb01.jpg"
                        }
                        alt={formation.nom_formation}
                        onError={(e) => {
                          e.target.src =
                            "assets/img/courses/course_thumb01.jpg";
                        }}
                      />
                    </div>

                    <div className="courses__details-content">
                      <ul className="courses__item-meta list-wrap">
                        <li className="courses__item-tag">
                          <a href="#">{formation.mode_livraison}</a>
                        </li>
                      </ul>
                      <h2 className="title">{formation.nom_formation}</h2>
                      <div className="courses__details-meta">
                        <ul className="list-wrap">
                          <li>
                            <i className="flaticon-calendar"></i>{" "}
                            {formation.duree_formation}
                          </li>
                        </ul>
                      </div>

                      <ul className="nav nav-tabs" id="myTab" role="tablist">
                        <li className="nav-item" role="presentation">
                          <button
                            className="nav-link active"
                            id="overview-tab"
                            data-bs-toggle="tab"
                            data-bs-target="#overview-tab-pane"
                            type="button"
                            role="tab"
                          >
                            Aperçu
                          </button>
                        </li>
                        <li className="nav-item" role="presentation">
                          <button
                            className="nav-link"
                            id="curriculum-tab"
                            data-bs-toggle="tab"
                            data-bs-target="#curriculum-tab-pane"
                            type="button"
                            role="tab"
                          >
                            Contenu
                          </button>
                        </li>
                      </ul>

                      <div className="tab-content" id="myTabContent">
                        <div
                          className="tab-pane fade show active"
                          id="overview-tab-pane"
                          role="tabpanel"
                        >
                          <div className="courses__overview-wrap">
                            <h3 className="title">Description du cours</h3>
                            <p>
                              {formation.description_formation ||
                                "Aucune description disponible."}
                            </p>
                          </div>
                        </div>

                        <div
                          className="tab-pane fade"
                          id="curriculum-tab-pane"
                          role="tabpanel"
                        >
                          <div className="courses__curriculum-wrap">
                            <h3 className="title">Contenu du cours</h3>
                            <p>
                              {formation.contenu_formation ||
                                "Le contenu détaillé n'est pas encore disponible."}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="col-xl-3 col-lg-4">
                    <div className="courses__details-sidebar">
                      <div className="courses__cost-wrap">
                        <span>Prix du cours:</span>
                        <h2 className="title">
                          {formation.prix_formation} GNF
                        </h2>
                      </div>

                      <div className="courses__information-wrap">
                        <h5 className="title">Le cours inclut:</h5>
                        <ul className="list-wrap">
                          <li>
                            <img
                              src="/assets/img/icons/course_icon02.svg"
                              alt="img"
                              className="injectable"
                            />{" "}
                            Durée du Cours{" "}
                            <span>{formation.duree_formation}</span>
                          </li>
                          <li>
                            <img
                              src="/assets/img/icons/course_icon05.svg"
                              alt="img"
                              className="injectable"
                            />{" "}
                            Mode de livraison{" "}
                            <span>{formation.mode_livraison}</span>
                          </li>
                        </ul>
                      </div>

                      <div className="courses__details-social">
                        <h5 className="title">Partager ce cours:</h5>
                        <ul className="list-wrap">
                          <li>
                            <a href="#">
                              <i className="fab fa-facebook-f"></i>
                            </a>
                          </li>
                          <li>
                            <a href="#">
                              <i className="fab fa-twitter"></i>
                            </a>
                          </li>
                          <li>
                            <a href="#">
                              <i className="fab fa-whatsapp"></i>
                            </a>
                          </li>
                          <li>
                            <a href="#">
                              <i className="fab fa-instagram"></i>
                            </a>
                          </li>
                        </ul>
                      </div>

                      <button
                        type="button"
                        className="btn btn-primary w-100"
                        onClick={() => navigate(`/registration/${id}`)}
                        disabled={loading}
                      >
                        {loading ? (
                          <>
                            <span
                              className="spinner-border spinner-border-sm me-2"
                              role="status"
                            ></span>{" "}
                            Chargement...
                          </>
                        ) : (
                          "S'inscrire"
                        )}
                      </button>

                      {/* Additional retry option in sidebar */}
                      {error && (
                        <div className="mt-3">
                          <button
                            className="btn btn-outline-secondary w-100 btn-sm"
                            onClick={handleRetry}
                            disabled={loading}
                          >
                            {loading ? (
                              <>
                                <span
                                  className="spinner-border spinner-border-sm me-2"
                                  role="status"
                                ></span>{" "}
                                Actualisation...
                              </>
                            ) : (
                              <>
                                <i className="fas fa-redo me-2"></i> Actualiser
                                les données
                              </>
                            )}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </>
        )}
      </main>
    </>
  );
};

export default CourseDetails;
